import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import messagingApi from '../../services/messagingApi';
import MessageStatsCards from './MessageStatsCards';
import MessageQueueTable from './MessageQueueTable';

const MessageQueueTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    page: 1,
    limit: 50
  });

  // Fetch stats and messages
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, queueData] = await Promise.all([
        messagingApi.getStats(),
        messagingApi.getQueue(filters)
      ]);

      setStats(statsData.stats);
      setMessages(queueData.messages || []);
      setPagination(queueData.pagination || {});
    } catch (err) {
      console.error('Error fetching messaging data:', err);
      setError('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleCancel = async (messageId) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذه الرسالة؟')) {
      return;
    }

    try {
      await messagingApi.cancelMessage(messageId);
      fetchData();
    } catch (err) {
      alert('فشل في إلغاء الرسالة: ' + err.message);
    }
  };

  const handleRetry = async (messageId) => {
    try {
      await messagingApi.retryMessage(messageId);
      fetchData();
    } catch (err) {
      alert('فشل في إعادة محاولة الرسالة: ' + err.message);
    }
  };

  if (loading && !stats) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div dir="rtl">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && <MessageStatsCards stats={stats} />}

      {/* Filters Bar */}
      <Row className="mb-4 align-items-end">
        <Col md={3}>
          <Form.Group>
            <Form.Label>الحالة</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              dir="rtl"
            >
              <option value="">الكل</option>
              <option value="pending">قيد الانتظار</option>
              <option value="sent">تم الإرسال</option>
              <option value="delivered">تم التوصيل</option>
              <option value="failed">فشل</option>
              <option value="cancelled">ملغي</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group>
            <Form.Label>القناة</Form.Label>
            <Form.Select
              value={filters.channel}
              onChange={(e) => handleFilterChange('channel', e.target.value)}
              dir="rtl"
            >
              <option value="">الكل</option>
              <option value="whatsapp">واتساب</option>
              <option value="sms">SMS</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={3}>
          <Button variant="outline-secondary" onClick={() => {
            setFilters({ status: '', channel: '', page: 1, limit: 50 });
          }}>
            إعادة تعيين الفلاتر
          </Button>
        </Col>

        <Col md={3} className="text-start">
          <Button variant="primary" onClick={fetchData} disabled={loading}>
            {loading ? 'جاري التحديث...' : 'تحديث'}
          </Button>
        </Col>
      </Row>

      {/* Message Queue Table/Cards */}
      <MessageQueueTable
        messages={messages}
        pagination={pagination}
        onPageChange={handlePageChange}
        onCancel={handleCancel}
        onRetry={handleRetry}
        loading={loading}
      />
    </div>
  );
};

export default MessageQueueTab;
